import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, Clock3, Play } from "lucide-react";
import { useState } from "react";
import { PoliticsQuestionOptions, PoliticsSolution } from "@/components/politics-question";
import { Button, EmptyState, LoadingLine, PageHeader, Tag } from "@/components/primitives";
import { apiFetch } from "@/lib/api";
import {
  PRACTICE_SOURCE_LABELS,
  QUESTION_TYPE_LABELS,
  SESSION_STATUS_LABELS,
  formatPoliticsAccuracy,
  formatPoliticsDate,
  politicsAccuracyPercentage,
  type PoliticsSession,
  type PoliticsSessionStatus,
  type PoliticsSessionSummary,
} from "@/lib/politics";

type HistoryStatus = "all" | PoliticsSessionStatus;

export function PoliticsHistoryPage() {
  const [status, setStatus] = useState<HistoryStatus>("all");
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ status, page: String(page), pageSize: "20" });
  const query = useQuery({
    queryKey: ["politics", "history", status, page],
    queryFn: () => apiFetch<{ sessions: PoliticsSessionSummary[]; page: number; pageSize: number; total: number }>(`/politics/history?${params}`),
  });
  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / (query.data?.pageSize ?? 20)));
  return (
    <div className="page politics-history-page">
      <PageHeader title="政治答题记录" description="回看每一组练习的范围、成绩和逐题结果。" />
      <div className="politics-history-filter" role="group" aria-label="记录状态">
        {(["all", "active", "completed", "abandoned"] as HistoryStatus[]).map((value) => (
          <button className={status === value ? "active" : ""} type="button" key={value} onClick={() => { setStatus(value); setPage(1); }}>
            {value === "all" ? "全部记录" : SESSION_STATUS_LABELS[value]}
          </button>
        ))}
      </div>
      {query.isLoading ? <LoadingLine /> : null}
      {query.isError ? <p className="politics-inline-notice">答题记录读取失败，请稍后重试。</p> : null}
      {query.data?.sessions.length ? (
        <section className="politics-history-list">
          {query.data.sessions.map((session) => {
            const accuracy = politicsAccuracyPercentage(session.correctCount, session.answeredCount);
            return (
              <article key={session.id}>
                <div className="politics-history-list__mark"><Clock3 /></div>
                <div>
                  <header>
                    <div className="tag-list">
                      <Tag tone={session.status === "completed" ? "success" : session.status === "active" ? "accent" : "neutral"}>{SESSION_STATUS_LABELS[session.status]}</Tag>
                      <Tag>{PRACTICE_SOURCE_LABELS[session.source]}</Tag>
                    </div>
                    <time>{formatPoliticsDate(session.startedAt)}</time>
                  </header>
                  <h2>{session.bankName}</h2>
                  <p>
                    {QUESTION_TYPE_LABELS[session.questionType]} · 已答 {session.answeredCount}/{session.actualCount} 题 ·
                    正确率 {formatPoliticsAccuracy(accuracy)}
                  </p>
                </div>
                {session.status === "active" ? (
                  <Link className="button button--primary button--md" to="/app/$subject/practice/$sessionId" params={{ subject: "politics", sessionId: session.id }}>
                    <Play /> 继续
                  </Link>
                ) : (
                  <Link className="button button--ghost button--md" to="/app/$subject/history/$sessionId" params={{ subject: "politics", sessionId: session.id }}>
                    查看详情 <ArrowRight />
                  </Link>
                )}
              </article>
            );
          })}
        </section>
      ) : !query.isLoading && !query.isError ? (
        <EmptyState
          title="还没有答题记录"
          description="完成或暂时退出一组政治练习后，记录会保留在这里。"
          action={<Link className="button button--primary button--md" to="/app/$subject/question-bank" params={{ subject: "politics" }}>开始第一组练习</Link>}
        />
      ) : null}
      {query.data && query.data.total > query.data.pageSize ? (
        <nav className="politics-pagination" aria-label="记录分页">
          <span>第 {query.data.page} / {totalPages} 页</span>
          <div>
            <Button size="sm" type="button" aria-label="上一页" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft /></Button>
            <Button size="sm" type="button" aria-label="下一页" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}><ChevronRight /></Button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}

export function PoliticsHistoryDetailPage() {
  const { sessionId } = useParams({ from: "/app/$subject/history/$sessionId" });
  const query = useQuery({
    queryKey: ["politics", "session", sessionId],
    queryFn: () => apiFetch<{ session: PoliticsSession }>(`/politics/sessions/${sessionId}`),
  });
  const session = query.data?.session;
  if (query.isLoading) return <div className="page"><LoadingLine /></div>;
  if (query.isError || !session) {
    return <div className="page"><EmptyState title="没有找到这条记录" description="它可能不存在，或不属于当前账号。" /></div>;
  }
  const accuracy = politicsAccuracyPercentage(session.correctCount, session.answeredCount);
  return (
    <div className="page politics-history-detail">
      <Link className="back-link" to="/app/$subject/history" params={{ subject: "politics" }}><ArrowLeft /> 返回答题记录</Link>
      <section className="politics-history-summary">
        <div>
          <p className="section-label">{SESSION_STATUS_LABELS[session.status]}</p>
          <h1>{session.bankName}</h1>
          <p>{PRACTICE_SOURCE_LABELS[session.source]} · {QUESTION_TYPE_LABELS[session.questionType]} · {formatPoliticsDate(session.startedAt)}</p>
        </div>
        <dl>
          <div><dt>已作答</dt><dd>{session.answeredCount}</dd></div>
          <div><dt>答对</dt><dd>{session.correctCount}</dd></div>
          <div><dt>正确率</dt><dd>{formatPoliticsAccuracy(accuracy)}</dd></div>
        </dl>
        {session.status === "active" ? (
          <Link className="button button--primary button--md" to="/app/$subject/practice/$sessionId" params={{ subject: "politics", sessionId }}><Play /> 继续练习</Link>
        ) : null}
      </section>
      <section className="politics-history-items" aria-label="逐题结果">
        {session.items.map((item) => item.question ? (
          <article key={item.id}>
            <header>
              <span>第 {item.queueOrder + 1} 题</span>
              {item.result
                ? <Tag tone={item.result.correct ? "success" : "accent"}>{item.result.correct ? "正确" : "错误"}</Tag>
                : <Tag>未作答</Tag>}
            </header>
            <h2>{item.question.stem}</h2>
            <PoliticsQuestionOptions
              question={item.question}
              selected={item.result?.selectedOptionKeys ?? []}
              result={item.result}
              disabled
            />
            {item.result ? <PoliticsSolution solution={item.result} result={item.result} /> : null}
          </article>
        ) : null)}
      </section>
      {session.items.length === 0 ? (
        <EmptyState title="本组没有题目" description="该记录没有可展示的题目。" />
      ) : null}
      {session.status === "completed" ? (
        <div className="politics-history-complete"><CheckCircle2 /><span>本组练习已完整保存</span></div>
      ) : null}
    </div>
  );
}
